import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Bell, UserPlus, Gift, Check, X, MessageCircle, Crown, Sparkles, AtSign, Award } from "lucide-react";import { api } from "@/App";
import { AdeLikeGlyph } from "@/components/AdeIcon";
import { toast } from "sonner";
import { AdeLogoIcon } from "@/components/AdeIcon";
import { GoldenRingAvatar } from "@/components/GoldenRingAvatar";
import { AdeLoadingSpinner } from "@/components/AdeLoadingSpinner";
import { useLang } from "@/i18n";
import { getNotificationRoute } from "@/utils/notificationRouting";
import { ProBadge } from "@/components/ProBadge";

// Unified Activity Feed — single chronological stream (Instagram/TikTok style)

const TYPE_ICON = {
  connection_request: { icon: UserPlus, color: "bg-blue-500" },
  connection_accepted: { icon: Check, color: "bg-green-500" },
  connection_declined: { icon: X, color: "bg-red-400" },
  gift_received: { icon: Gift, color: "bg-amber-500" },
  golden_ring: { icon: Crown, color: "bg-yellow-500" },
  creator_reward_available: { icon: Award, color: "bg-emerald-500" },
  like: { icon: AdeLikeGlyph, color: "bg-gradient-to-r from-orange-500 to-pink-500" },
  comment: { icon: MessageCircle, color: "bg-cyan-500" },
  story_reaction: { icon: Sparkles, color: "bg-orange-500" },
  story_comment: { icon: MessageCircle, color: "bg-fuchsia-500" },
  story_interaction: { icon: Sparkles, color: "bg-fuchsia-500" },
  mention: { icon: AtSign, color: "bg-violet-500" },
};

export default function Alerts() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setNotifications(res.data.notifications || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    api.post("/notifications/read-all").catch(() => {});
    api.get("/chat/unread/count")
      .then((res) => setUnreadMessages(res.data.count || 0))
      .catch(() => {});
  }, [fetchNotifications]);

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    await fetchNotifications();
    setTimeout(() => setIsRefreshing(false), 2000);
  };

  const getImageUrl = (url) => {
    if (!url) return "";
    return url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  // Compact time: 2m, 1h, 3h, 1d, 5d, 2w
  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "ora";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d`;
    const weeks = Math.floor(days / 7);
    return `${weeks}w`;
  };

  // Notification text — natural language
  const getText = (n) => {
    const user = n.related_user || n.from_user;
    const name = user?.name || "Qualcuno";
    const source = n.data?.source;

    switch (n.type) {
      case "connection_request":
      case "island_doorbell":
        if (source === "island_doorbell" || n.type === "island_doorbell") {
          return <><strong>{name}</strong> ha suonato alla tua ADE Island</>;
        }
        return <><strong>{name}</strong> ti ha inviato una richiesta di collegamento</>;
      case "connection_accepted":
        return <><strong>{name}</strong> ha accettato la tua richiesta di collegamento</>;
      case "gift_received": {
        if (n.data?.requires_reveal) {
          const forPost = n.data?.photo_id ? " per il tuo post" : "";
          return <><strong>{name}</strong> ti ha inviato un regalo{forPost} — scoprilo</>;
        }
        const giftName = n.data?.gift_name || "una Gift";
        return <><strong>{name}</strong> ti ha inviato {giftName}</>;
      }
      case "golden_ring":
        return <><strong>{name}</strong> ti ha inviato un Golden Ring</>;
      case "golden_ring_verified":
        return <>{n.message || "La tua verifica Golden Ring è stata approvata"}</>;
      case "creator_reward_available":
        return <>{n.message || "Un Creator Reward è ora disponibile"}</>;
      case "like":
        return <><strong>{name}</strong> ha messo AdE Like al tuo post</>;
      case "comment": {
        const preview = n.preview || n.message || "";
        return <><strong>{name}</strong> ha commentato: &quot;{preview.slice(0, 40)}&quot;</>;
      }
      case "story_comment": {
        const preview = n.preview || n.message || "";
        return <><strong>{name}</strong> ha commentato la tua storia: &quot;{preview.slice(0, 40)}&quot;</>;
      }
      case "story_interaction": {
        const st = n.data?.sticker_type;
        const label = st === "poll" ? "ha votato nel tuo sondaggio"
          : st === "question" ? "ha risposto alla tua domanda"
          : "ha usato il tuo slider";
        return <><strong>{name}</strong> {label}</>;
      }
      case "story_reaction": {
        const emoji = n.data?.emoji;
        if (!emoji || emoji === "ADE_LIKE") return <><strong>{name}</strong> ha messo AdE Like alla tua storia</>;
        return <><strong>{name}</strong> ha reagito alla tua storia</>;
      }
      case "message": {
        const preview = n.data?.preview || n.preview || "";
        return preview
          ? <><strong>{name}</strong> ti ha scritto: &quot;{preview.slice(0, 40)}&quot;</>
          : <><strong>{name}</strong> ti ha scritto un messaggio</>;
      }
      case "professional_follow":
        return <><strong>{name}</strong> si è collegato al tuo profilo ADE Pro</>;
      case "tagged_in_post":
        return <><strong>{name}</strong> ti ha menzionato in un post</>;
      case "mention": {
        const ctx = n.data?.mention_context;
        return <><strong>{name}</strong> ti ha menzionato in {ctx === "comment" ? "un commento" : ctx === "story_reply" ? "una risposta a un mood" : "un post"}</>;
      }
      case "missed_call":
        return <><strong>{name}</strong> ti ha chiamato</>;
      case "creator_approved":
        return <>La tua richiesta Creator è stata approvata</>;
      case "payout_completed":
        return <>Il tuo payout è stato completato</>;
      default:
        return n.message || <><strong>{name}</strong> ha interagito con il tuo profilo</>;
    }
  };

  // Notification text WITHOUT the name (name rendered separately for click handling)
  const getTextWithoutName = (n) => {
    const source = n.data?.source;
    switch (n.type) {
      case "connection_request":
      case "island_doorbell":
        if (source === "island_doorbell" || n.type === "island_doorbell") {
          return "ha suonato alla tua ADE Island";
        }
        return "ti ha inviato una richiesta di collegamento";
      case "connection_accepted":
        return "ha accettato la tua richiesta di collegamento";
      case "gift_received": {
        if (n.data?.requires_reveal) return <>ti ha inviato un regalo — scoprilo</>;
        const giftName = n.data?.gift_name || "una Gift";
        return <>ti ha inviato {giftName}</>;
      }
      case "golden_ring":
        return "ti ha inviato un Golden Ring";
      case "golden_ring_verified":
        return n.message || "La tua verifica Golden Ring è stata approvata";
      case "creator_reward_available":
        return n.message || "Un Creator Reward è ora disponibile";
      case "like":
        return "ha messo AdE Like al tuo post";
      case "comment": {
        const preview = n.data?.preview || n.preview || n.message || "";
        return <>ha commentato: &quot;{preview.slice(0, 40)}&quot;</>;
      }
      case "story_comment": {
        const preview = n.preview || n.message || "";
        return <>ha commentato la tua storia: &quot;{preview.slice(0, 40)}&quot;</>;
      }
      case "story_interaction": {
        const st = n.data?.sticker_type;
        return st === "poll" ? "ha votato nel tuo sondaggio"
          : st === "question" ? "ha risposto alla tua domanda"
          : "ha usato il tuo slider";
      }
      case "story_reaction": {
        const emoji = n.data?.emoji;
        if (!emoji || emoji === "ADE_LIKE") return "ha messo AdE Like alla tua storia";
        return "ha reagito alla tua storia";
      }
      case "message": {
        const preview = n.data?.preview || n.preview || "";
        return preview
          ? <>ti ha scritto: &quot;{preview.slice(0, 40)}&quot;</>
          : "ti ha scritto un messaggio";
      }
      case "professional_follow":
        return "si è collegato al tuo profilo ADE Pro";
      case "tagged_in_post":
        return <>ti ha menzionato in un post</>;
      case "mention": {
        const ctx = n.data?.mention_context;
        return <>ti ha menzionato in {ctx === "comment" ? "un commento" : ctx === "story_reply" ? "una risposta a un mood" : "un post"}</>;
      }
      case "missed_call":
        return "ti ha chiamato";
      case "creator_approved":
        return "— richiesta Creator approvata";
      case "payout_completed":
        return "— payout completato";
      default:
        return n.message || "ha interagito con il tuo profilo";
    }
  };


  const handleClick = (n) => {
    navigate(getNotificationRoute(n));
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="page-content bg-white" data-testid="alerts-page">
      {/* Header — minimal */}
      <div className="sticky top-0 safe-top bg-white/95 backdrop-blur-sm z-10 px-4 pt-4 pb-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="focus:outline-none active:scale-95 transition-transform"
            data-testid="ade-refresh-btn"
          >
            <img
              src="/images/ade-logo-extracted.png"
              alt=""
              className={`object-contain drop-shadow-lg transition-transform ${
                isRefreshing ? "animate-spin-slow" : "hover:scale-105"
              }`}
              style={{ width: 48, height: 48 }}
            />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900">
              {t("notifications")}
            </h1>
            {unreadCount > 0 && (
              <p className="text-xs text-orange-500 font-medium">
                {unreadCount} nuove
              </p>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={() => {
                api.post("/notifications/read-all");
                setNotifications((prev) =>
                  prev.map((n) => ({ ...n, read: true }))
                );
              }}
              className="text-xs text-gray-400 hover:text-gray-600 px-3 py-1.5 rounded-full bg-gray-100"
              data-testid="mark-all-read-btn"
            >
              {t("markAllRead")}
            </button>
          )}
          {/* Messages button - top right with unread counter */}
          <button
            onClick={() => navigate("/app/messages")}
            className="relative p-2 rounded-full hover:bg-gray-100 active:scale-95 transition-all"
            data-testid="alerts-messages-btn"
          >
            <MessageCircle className="w-6 h-6 text-gray-700" strokeWidth={1.8} />
            {unreadMessages > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-gradient-to-r from-orange-500 to-pink-500 text-white text-[10px] font-bold rounded-full px-1 shadow-lg"
                data-testid="alerts-messages-badge"
              >
                {unreadMessages > 99 ? "99+" : unreadMessages}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Unified Feed */}
      {loading ? (
        <div className="flex justify-center py-16">
          <AdeLoadingSpinner size="lg" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-20 px-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            <Bell className="w-7 h-7 text-gray-300" />
          </div>
          <p className="text-gray-400 text-sm">{t("noNotifications")}</p>
          <p className="text-gray-300 text-xs mt-1">
            {t("noNotificationsMsg")}
          </p>
        </div>
      ) : (
        <div>
          {notifications.map((n) => {
            const user = n.related_user || n.from_user;
            const typeConfig = TYPE_ICON[n.type] || TYPE_ICON.connection_request;
            const TypeIcon = typeConfig.icon;
            const userId = n.related_user_id || user?.user_id;

            const goToProfile = (e) => {
              e.stopPropagation();
              if (userId) navigate(`/app/user/${userId}`);
            };

            const goToObject = () => {
              navigate(getNotificationRoute(n));
            };

            const isSystem = n.type === "creator_approved" || n.type === "payout_completed";

            return (
              <div
                key={n.notification_id || n.id}
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                  !n.read ? "bg-orange-50/40" : ""
                }`}
                data-testid={`notification-${n.notification_id || n.id}`}
              >
                {/* Avatar — click goes to PROFILE (system notifs show ADE badge instead of avatar) */}
                <div className={`relative shrink-0 ${isSystem ? '' : 'cursor-pointer'}`} onClick={isSystem ? undefined : goToProfile}>
                  {isSystem ? (
                    <div className={`w-11 h-11 rounded-full ${typeConfig.color} flex items-center justify-center`}>
                      <TypeIcon className="w-5 h-5 text-white" />
                    </div>
                  ) : user?.golden?.level > 0 ? (
                    <GoldenRingAvatar user={user} size={44} />
                  ) : (
                    <Avatar className="w-11 h-11">
                      {user?.picture ? (
                        <AvatarImage
                          src={getImageUrl(user.picture)}
                          alt={user.name}
                        />
                      ) : (
                        <AvatarFallback className="bg-gray-100 text-gray-400 text-sm font-medium">
                          {user?.name?.charAt(0)?.toUpperCase() || "?"}
                        </AvatarFallback>
                      )}
                    </Avatar>
                  )}
                  {!isSystem && (
                    <div
                      className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full ${typeConfig.color} flex items-center justify-center ring-2 ring-white`}
                    >
                      <TypeIcon className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>

                {/* Text — click goes to OBJECT */}
                <div className="flex-1 min-w-0 cursor-pointer active:bg-gray-50 rounded-lg -my-1 py-1 px-1 -mx-1" onClick={goToObject}>
                  <p className="text-sm text-gray-800 leading-snug">
                    {isSystem ? (
                      getTextWithoutName(n)
                    ) : (
                      <>
                        <strong className="cursor-pointer hover:underline" onClick={goToProfile}>
                          {user?.name || "Utente"}
                        </strong>
                        <ProBadge user={user} size="xs" showLabel={false} className="ml-1 align-middle" />
                        {" "}{getTextWithoutName(n)}
                      </>
                    )}
                  </p>
                  <span className="text-xs text-gray-400">
                    {timeAgo(n.created_at)}
                  </span>
                </div>

                {/* Right side: post thumbnail or gift image — click goes to OBJECT */}
                <div className="cursor-pointer shrink-0" onClick={goToObject}>
                  {n.type === "gift_received" && n.data?.gift_image ? (
                    <div className="w-11 h-11 rounded-lg bg-amber-50 flex items-center justify-center">
                      <img src={n.data.gift_image} alt="" className="w-8 h-8 object-contain" />
                    </div>
                  ) : n.photo_url ? (
                    <div className="w-11 h-11 rounded-lg overflow-hidden bg-gray-100">
                      <img src={getImageUrl(n.photo_url)} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : null}
                </div>

                {/* Unread dot */}
                {!n.read && (
                  <div className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
